<script setup>
import { ref, onMounted } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import LiveChatCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/LiveChatCampaign/LiveChatCampaignForm.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const confirmCloseRef = ref(null);
const formRef = ref(null);
const saving = ref(false);
const saveError = ref('');

const addCampaign = async campaignDetails => {
  if (saving.value) return;
  saving.value = true;
  saveError.value = '';
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    emit('close');

    // tracking this here instead of the store to track the type of campaign
    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONGOING,
    });

    useAlert(t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.API.ERROR_MESSAGE');
    saveError.value = errorMessage;
  } finally {
    saving.value = false;
  }
};

const handleClose = () => {
  if (saving.value) return;
  if (formRef.value?.isDirty?.()) confirmCloseRef.value?.open();
  else emit('close');
};

const handleSubmit = campaignDetails => {
  addCampaign(campaignDetails);
};
const confirmAbandon = () => {
  if (!saving.value) emit('close');
};
onMounted(() => dialogRef.value?.open?.());
defineExpose({ requestClose: handleClose });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.LIVE_CHAT.CREATE.TITLE')"
    width="2xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    confirm-before-close
    @request-close="handleClose"
    @close="emit('close')"
  >
    <p v-if="saveError" role="alert" class="text-sm text-n-ruby-11">
      {{ saveError }}
    </p>
    <fieldset class="m-0 min-w-0 border-0 p-0" :disabled="saving">
      <LiveChatCampaignForm
        ref="formRef"
        mode="create"
        @submit="handleSubmit"
        @cancel="handleClose"
      />
    </fieldset>
  </Dialog>
  <Dialog
    ref="confirmCloseRef"
    type="alert"
    :title="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.TITLE')"
    :description="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.CONFIRM')"
    :cancel-button-label="t('CAMPAIGN.LIVE_CHAT.CREATE.ABANDON.CANCEL')"
    @confirm="confirmAbandon"
  />
</template>
